import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { performanceService } from "../../api/performanceService";
import BackLink from "../../components/ui/BackLink";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatManilaDateTime } from "../../utils/manilaTime";
import { cx } from "../../utils/classNames";
import {
  EMPLOYEE_RESPONSE,
  EMPLOYEE_STATUS,
  hasAnyAnswer,
  invalidatePerformance,
} from "./performanceUtils";
import { useEvaluation } from "./usePerformanceQueries";
import QueryState from "./components/QueryState";
import EvaluationHeader from "./components/EvaluationHeader";
import EvaluationEditor from "./components/EvaluationEditor";
import EvaluationResults from "./components/EvaluationResults";
import EvaluationForm from "./form/EvaluationForm";
import ActivityTimeline from "./components/ActivityTimeline";
import AcknowledgeDialog from "./components/AcknowledgeDialog";
import ui from "../../components/ui/ui.module.css";

const MyEvaluationDetailView = () => {
  const { id } = useParams();
  const query = useEvaluation(id);

  return (
    <div className={ui.page}>
      <div className={cx(ui.pageHeader, "no-print")}>
        <div>
          <BackLink fallback="/me/evaluations" label="My evaluations" />
          <h4 className="page-title">
            {query.data?.template?.name ?? "Evaluation"}
          </h4>
        </div>
      </div>

      <QueryState query={query} loadingText="Loading evaluation…">
        {(evaluation) => (
          <EvaluationBody key={evaluation.id} evaluation={evaluation} />
        )}
      </QueryState>
    </div>
  );
};

// What the employee sees depends on where the evaluation is in the workflow.
const EvaluationBody = ({ evaluation }) => {
  const navigate = useNavigate();
  const { status, permissions } = evaluation;

  return (
    <>
      <EvaluationHeader evaluation={evaluation} statusMap={EMPLOYEE_STATUS} />

      {status === "PENDING_SELF" && permissions?.canEditSelf && (
        <>
          <div className={cx(ui.alert, ui.alertWarning)}>
            <span>
              <strong>Your self-assessment is due.</strong> Rate yourself on
              each item. Your answers save automatically; submit when you're
              done.
            </span>
          </div>
          <EvaluationEditor
            evaluation={evaluation}
            initialResponses={evaluation.selfResponses}
            save={(responses) =>
              performanceService.saveSelfAssessment(evaluation.id, responses)
            }
            submit={() =>
              performanceService.submitSelfAssessment(evaluation.id)
            }
            submitLabel="Submit self-assessment"
            confirmTitle="Submit self-assessment?"
            confirmMessage="You can't change it after submitting."
            successMessage="Self-assessment submitted. Your evaluator will take it from here."
            onSubmitted={() => navigate("/me/evaluations")}
          />
        </>
      )}

      {status === "PENDING_EVALUATION" && (
        <>
          <div className={cx(ui.alert, ui.alertInfo, ui.alertColumn)}>
            <span>
              <strong>In progress.</strong> Your evaluator is rating you; you'll
              see the result here when they submit.
            </span>
            {evaluation.employeeResponse === "CONTEST" &&
              evaluation.contestResolution && (
                <span>
                  After your contest, HR sent it back to your evaluator: “
                  {evaluation.contestResolution}”
                </span>
              )}
          </div>
          {hasAnyAnswer(evaluation.selfResponses) && (
            <>
              <h6 className="mb-0">Your self-assessment</h6>
              <EvaluationForm
                sections={evaluation.sections}
                responses={evaluation.selfResponses}
              />
            </>
          )}
        </>
      )}

      {[
        "PENDING_ACKNOWLEDGEMENT",
        "PENDING_SIGN_OFF",
        "CONTESTED",
        "COMPLETED",
      ].includes(status) && (
        <>
          <AcknowledgementPanel evaluation={evaluation} />
          <EvaluationResults evaluation={evaluation} />
        </>
      )}

      {status === "CANCELLED" && (
        <div className={cx(ui.alert, ui.alertGray)}>
          This evaluation was cancelled by HR.
        </div>
      )}

      <div className="no-print">
        <ActivityTimeline activities={evaluation.activities} />
      </div>
    </>
  );
};

// The employee agrees with or contests the result (comment required).
// CONTESTED waits for HR, who upholds it (COMPLETED) or sends it back for
// re-evaluation (it comes back here later).
const AcknowledgementPanel = ({ evaluation }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [response, setResponse] = useState(null); // null | "ACCEPT" | "CONTEST"

  const mutation = useMutation({
    mutationFn: ({ response: answer, comment }) =>
      performanceService.acknowledge(evaluation.id, {
        response: answer,
        comment,
      }),
    onSuccess: (_, { response: answer }) => {
      toast.success(
        answer === "ACCEPT"
          ? "Thank you. You agreed with the evaluation."
          : "Your contest was sent to HR. You'll be notified of the decision.",
      );
      setResponse(null);
      invalidatePerformance(queryClient);
    },
    onError: (error) => {
      if (error?.response?.status === 409) {
        toast.error("This evaluation was changed elsewhere. Reloading…");
        setResponse(null);
        invalidatePerformance(queryClient);
      }
    },
  });

  const dialogError =
    mutation.error && mutation.error.response?.status !== 409
      ? getErrorMessage(mutation.error, "Unable to save your answer.")
      : "";

  const answer = EMPLOYEE_RESPONSE[evaluation.employeeResponse];
  const yourAnswer = answer && evaluation.employeeComment && (
    <span>
      You {answer.label.toLowerCase()}{" "}
      {evaluation.employeeResponse === "ACCEPT" ? "with " : ""}this evaluation
      {evaluation.acknowledgedAt &&
        ` on ${formatManilaDateTime(evaluation.acknowledgedAt)}`}
      : “{evaluation.employeeComment}”
    </span>
  );

  if (evaluation.status === "COMPLETED") {
    return (
      <div className={cx(ui.alert, ui.alertSuccess, ui.alertColumn)}>
        <div className={cx(ui.pageHeader, "w-100")}>
          <span>
            <strong>Completed.</strong>
            {!answer &&
              evaluation.acknowledgedAt &&
              ` Acknowledged on ${formatManilaDateTime(evaluation.acknowledgedAt)}.`}
          </span>
          <button
            type="button"
            className={cx(ui.button, ui.secondary, ui.buttonSmall, "no-print")}
            onClick={() => window.print()}
          >
            <i className="bi bi-printer"></i>
            Print
          </button>
        </div>
        {answer
          ? yourAnswer
          : evaluation.employeeComment && (
              <span>Your comment: “{evaluation.employeeComment}”</span>
            )}
        {evaluation.employeeResponse === "CONTEST" &&
          evaluation.contestResolution && (
            <span>
              <strong>HR kept the result:</strong> “
              {evaluation.contestResolution}”
            </span>
          )}
      </div>
    );
  }

  if (evaluation.status === "CONTESTED") {
    return (
      <div className={cx(ui.alert, ui.alertInfo, ui.alertColumn)}>
        <span>
          <strong>Contested – waiting for HR.</strong> HR will either keep the
          result or send it back to your evaluator.
        </span>
        {yourAnswer}
      </div>
    );
  }

  return (
    <>
      <div className={cx(ui.alert, ui.alertWarning, ui.alertColumn)}>
        <div className={cx(ui.pageHeader, "w-100")}>
          <span>
            <strong>Waiting for your answer.</strong> Review the results below,
            then agree or contest.
          </span>
          {evaluation.permissions?.canAcknowledge && (
            <span className={ui.actions}>
              <button
                type="button"
                className={cx(ui.button, ui.primary, ui.buttonSmall)}
                onClick={() => setResponse("ACCEPT")}
              >
                <i className="bi bi-check2-circle"></i>
                Agree
              </button>
              <button
                type="button"
                className={cx(ui.button, ui.secondary, ui.buttonSmall)}
                onClick={() => setResponse("CONTEST")}
              >
                <i className="bi bi-flag"></i>
                Contest
              </button>
            </span>
          )}
        </div>
        {evaluation.contestResolution && (
          <span>
            Your evaluator re-rated this after your contest. HR's note: “
            {evaluation.contestResolution}”
          </span>
        )}
      </div>

      {response && (
        <AcknowledgeDialog
          key={response}
          response={response}
          isPending={mutation.isPending}
          error={dialogError}
          onConfirm={(comment) => mutation.mutate({ response, comment })}
          onClose={() => {
            mutation.reset();
            setResponse(null);
          }}
        />
      )}
    </>
  );
};

export default MyEvaluationDetailView;
